export type Mount = '/orion-web' | '/symphony-web' | '';

// The single source of truth for which mount a request is on. Two path mounts
// live on the shared manage.wix.com host; Symphony has its own dedicated host
// and is served from its ROOT:
//
//   /orion-web     → Orion design, "Orion" name        (manage.wix.com)
//   /symphony-web  → Orion design, "Symphony" name      (manage.wix.com)
//   ''  (root)     → Symphony design                    (symphony.wix.com)
//
// Callers derive brand, product name, and base path from the mount instead of
// re-testing the URL. The root mount is the empty string because Symphony pages
// live directly at the host root with NO prefix — `${mount}/api/x` is `/api/x`
// and a `${mount}` link base is the bare host.
//
// Symphony is resolved by HOST, not path: at the root there is no path segment
// to key on, and its host serves only Symphony. Every other host resolves by
// path segment (anchored to the FIRST segment so a nested slug can't be taken
// for a mount). Never throws — anything unrecognized resolves to `/orion-web`,
// the base API/asset mount every manage.wix.com surface falls back to.
//
// `hostHint` is for SSR, where the host can't be read off `window`: pass the
// `x-forwarded-host` / `host` header. On the client the host comes off
// `window.location`; a full URL (the SSR forwarded-URL header) carries its own.
export function resolveMount(pathOrUrl: string | undefined | null, hostHint?: string | null): Mount {
  // `||` (not `??`): an empty-string hostHint means "no host header" (callers
  // build it as `x-forwarded-host || host`, which is '' when both are absent), so
  // it must fall through to the URL host / browser host — not be treated as a
  // real value. Keeps this in lockstep with onboarding-gate.js's mirror.
  const host = hostHint || hostFromUrl(pathOrUrl) || browserHost();
  if (isSymphonyHost(host)) return '';
  const path = toPathname(pathOrUrl);
  if (/^\/symphony-web(?:\/|$)/.test(path)) return '/symphony-web';
  if (/^\/orion-web(?:\/|$)/.test(path)) return '/orion-web';
  // Dev convenience: `next dev` runs on plain localhost, which can't carry a real
  // host like symphony.wix.com. Symphony is the flagship, so the bare host renders
  // the Symphony root — the `/orion-web` and `/symphony-web` paths above still
  // select those mounts for testing them. Checked AFTER the paths, and NODE_ENV-
  // guarded, so production can never resolve root from a stray localhost.
  if (isDevLocalhost(host)) return '';
  return '/orion-web';
}

// Only the real production host. (Dev renders the Symphony root on bare localhost
// via isDevLocalhost, so there's no dev host to special-case here.)
export function isSymphonyHost(host: string | undefined | null): boolean {
  return bareHost(host) === 'symphony.wix.com';
}

function isDevLocalhost(host: string | undefined | null): boolean {
  if (process.env.NODE_ENV !== 'development') return false;
  const h = bareHost(host);
  return h === 'localhost' || h === '127.0.0.1';
}

function bareHost(host: string | undefined | null): string {
  return host ? host.split(':')[0].toLowerCase() : '';
}

function browserHost(): string | undefined {
  return typeof window !== 'undefined' ? window.location.hostname : undefined;
}

// Host (with any port) out of a full URL; undefined for a bare path.
function hostFromUrl(pathOrUrl: string | undefined | null): string | undefined {
  if (!pathOrUrl) return undefined;
  const match = /^[a-z][a-z0-9+.-]*:\/\/([^/?#]+)/i.exec(pathOrUrl);
  return match ? match[1] : undefined;
}

// Reduce a bare path or full URL to its pathname (no scheme/host, no query/hash)
// so segment matching is never fooled by the host or a query value.
function toPathname(pathOrUrl: string | undefined | null): string {
  if (!pathOrUrl) return '/';
  let p = pathOrUrl.split('?')[0].split('#')[0];
  const scheme = p.indexOf('://');
  if (scheme !== -1) {
    const slash = p.indexOf('/', scheme + 3);
    p = slash === -1 ? '/' : p.slice(slash);
  }
  return p || '/';
}
